import * as React from 'react'
import moment from 'moment'
import { API, IProductLog, IProductLogEvent, ProductLogEventType } from '@getgreenline/homi-shared'
import { Timeline, Tag, Typography, Skeleton, Empty, message, Button } from 'antd'
import { GrPrice, parseErrorMsg } from '../../../../utilities/helpers'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { inject } from 'mobx-react'
interface Props {
  companyId: number
  productId?: string
  eventType?: ProductLogEventType
  defaultLimit?: number
  currentCompanyStore?: CurrentCompanyStore
}

interface State {
  isLoading: boolean
  productLogs?: IProductLog[]
  limit: number
}

function formatLogEvent(event: IProductLogEvent): string | undefined {
  switch (event.field_name) {
    case 'price':
      return `${event.location?.name || 'Retail'} price: ${GrPrice.convertCentToDollar(
        parseInt(event.old_value) || 0,
      )} to ${GrPrice.convertCentToDollar(parseInt(event.new_value) || 0)}`
    case 'purchasePrice':
      return `Wholesale cost: ${GrPrice.convertCentToDollar(
        parseInt(event.old_value) || 0,
      )} to ${GrPrice.convertCentToDollar(parseInt(event.new_value) || 0)}`
    case 'categoryId':
      return `Category`
    case `supplierId`:
      return `Brand`
    case `taxGroupId`:
      return `Tax group`
    case `pricingGroupId`:
      return `Pricing group`
    case `description`:
      return `Description`
    case `shortDescription`:
      return `Receipt description`
    case `thc`:
      return `THC: ${event.old_value || '-'} to ${event.new_value || '-'}`
    case `cbd`:
      return `CBD: ${event.old_value || '-'} to ${event.new_value || '-'}`
    case `barcode`:
      return `Barcode: ${event.old_value || '-'} to ${event.new_value || '-'}`
    case `shortName`:
      return `Barcode label`
    case `weight`:
      return `Net product weight: ${event.old_value || 0} to ${event.new_value || 0}`
    case `cannabisWeight`:
      return `Cannabis weight: ${event.old_value || 0} to ${event.new_value || 0}`
    case `cannabisVolume`:
      return `Cannabis volume: ${event.old_value || 0} to ${event.new_value || 0}`
    case `isActive`:
      return `Active: ${event.old_value} to ${event.new_value}`
    case `isBatchTracked`:
      return `Lot tracking: ${event.old_value} to ${event.new_value}`
    case `trackInventory`:
      return `Inventory tracking: ${event.old_value} to ${event.new_value}`
    case `imageUrl`:
      return `Image`
    case `sku`:
      return `SKU: ${event.old_value || '-'} to ${event.new_value || '-'}`
    case `name`:
      return `Name: ${event.old_value || '-'} to ${event.new_value || '-'}`
    case `isDeleted`:
      return `Deleted: ${event.old_value} to ${event.new_value}`
    default:
      return event.field_name
  }
}

const defaultLimit = 10

@inject('currentCompanyStore')
export class ProductLog extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      isLoading: false,
      limit: props.defaultLimit || defaultLimit,
    }
  }

  async componentDidMount() {
    this.loadProductLogs()
  }

  componentDidUpdate(prevProps: Props) {
    if (
      prevProps.productId !== this.props.productId ||
      prevProps.eventType !== this.props.eventType
    ) {
      this.loadProductLogs(true)
    }
  }

  async loadProductLogs(showLoading = false) {
    const { companyId, productId, eventType } = this.props
    const { limit } = this.state
    try {
      this.setState({
        isLoading: true,
        productLogs: showLoading ? undefined : this.state.productLogs,
      })
      const productLogs = await API.getProductLogs(companyId, {
        productIds: productId ? [productId] : undefined,
        events: eventType ? [eventType] : undefined,
        offset: 0,
        limit: limit,
      })
      this.setState({ productLogs, isLoading: false })
    } catch (error) {
      this.setState({ isLoading: false })
      message.error(parseErrorMsg(error) || 'There was an error fetching edit history')
    }
  }

  render() {
    const { productId } = this.props
    const { productLogs, limit, isLoading } = this.state

    if (!productLogs) {
      return <Skeleton active />
    }

    const paginatedProductLogs = productLogs.slice(0, limit - 1)

    return (
      <>
        <Timeline className='mt-1'>
          {productLogs.length === 0 && <Empty description='No edit history' />}
          {paginatedProductLogs.map((history) => {
            const event = history.event

            let eventColor = '#1890FF' // Update default color
            if (event === 'create') {
              eventColor = 'green'
            }
            if (event === 'delete') {
              eventColor = 'red'
            }

            return (
              <Timeline.Item key={history.id} color={eventColor}>
                {!productId && <div>{history.productName}</div>}
                <div className='mb-1'>
                  <Typography.Text>
                    {history.event}d by {history.userName} on{' '}
                    {moment(history.createDate).format('MMM D, YYYY h:mma')}
                  </Typography.Text>
                </div>
                {!!history.events &&
                  history.events.map((event) => {
                    return (
                      <Tag key={event.id} color='blue'>
                        {formatLogEvent(event)}
                      </Tag>
                    )
                  })}
              </Timeline.Item>
            )
          })}
          {productLogs.length === limit && (
            <Button
              loading={isLoading}
              onClick={() =>
                this.setState({ limit: limit + (this.props.defaultLimit || defaultLimit) }, () =>
                  this.loadProductLogs(),
                )
              }
            >
              Show more
            </Button>
          )}
        </Timeline>
      </>
    )
  }
}
